import { useState, useEffect, useCallback, useMemo } from 'react';

import { get } from '../services/apiClient';

import { usePermissions } from './usePermissions';

// Types for analytics data
export interface AnalyticsMetrics {
  users: {
    total: number;
    active: number;
    new_this_month: number;
    retention_rate: number;
  };
  evaluations: {
    total: number;
    completed: number;
    in_progress: number;
    average_score: number;
  };
  engagement: {
    login_frequency: number;
    feature_usage: Record<string, number>;
    satisfaction_score: number;
  };
}

export interface RoleBasedAnalyticsOptions {
  dateRange?: {
    start: Date;
    end: Date;
  };
  schoolId?: string | number;
  districtId?: string;
  includeSubordinates?: boolean;
}

export interface CommunicationAnalyticsMetrics {
  messages_sent: number;
  messages_received: number;
  response_rate: number;
  engagement_score: number;
}

export interface DashboardMetrics {
  scope: 'district' | 'school';
  users: {
    total: number;
    active: number;
  };
  evaluations: {
    total: number;
    completed: number;
    pending: number;
  };
  schools: {
    total: number;
  };
  teachers: {
    total: number;
  };
  recent_activity: Array<{
    id: string;
    type: 'evaluation' | 'user';
    description: string;
    timestamp: string;
  }>;
}

// Role-based analytics hook
export function useAnalytics(options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS) {
  const [data, setData] = useState<AnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, isTeacher, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const fetchAnalytics = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      // For development, provide mock data instead of making API calls
      // This prevents dashboard errors when there's no backend API
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      
      // Use options to potentially adjust mock data (for consistency)
      const baseTotal = options.schoolId ? 50 : options.districtId ? 300 : 150;
      
      const mockData: AnalyticsMetrics = {
        users: {
          total: baseTotal,
          active: Math.floor(baseTotal * 0.8),
          new_this_month: Math.floor(baseTotal * 0.1),
          retention_rate: 0.85,
        },
        evaluations: {
          total: Math.floor(baseTotal * 0.3),
          completed: Math.floor(baseTotal * 0.2),
          in_progress: Math.floor(baseTotal * 0.08),
          average_score: 3.7,
        },
        engagement: {
          login_frequency: 4.2,
          feature_usage: {
            dashboard: Math.floor(baseTotal * 0.8),
            reports: Math.floor(baseTotal * 0.57),
            evaluations: Math.floor(baseTotal * 0.63),
            communications: Math.floor(baseTotal * 0.4),
          },
          satisfaction_score: 4.1,
        },
      };

      setData(mockData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch analytics'
      );
    } finally {
      setLoading(false);
    }
  }, [user, options]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    data,
    loading,
    error,
    refresh: fetchAnalytics,
    canViewDistrict: isDistrictAdmin,
    canViewSchool: isSchoolAdmin || isDistrictAdmin,
    canViewTeacher: isTeacher || isSchoolAdmin || isDistrictAdmin,
  };
}

// Stable empty object to prevent re-creation
const EMPTY_OPTIONS: RoleBasedAnalyticsOptions = {};

// Administrative dashboard metrics. Scope is derived by the API from the
// authenticated user; callers cannot request another tenant or school.
export function useDashboardMetrics() {
  const [data, setData] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const fetchDashboardMetrics = useCallback(async () => {
    if (!user || (!isSchoolAdmin && !isDistrictAdmin)) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      setData(await get<DashboardMetrics>('/analytics/dashboard'));
    } catch (err) {
      setData(null);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to fetch dashboard analytics'
      );
    } finally {
      setLoading(false);
    }
  }, [user, isSchoolAdmin, isDistrictAdmin]);

  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  return {
    data,
    loading,
    error,
    refresh: fetchDashboardMetrics,
  };
}

// Communication analytics hook
export function useCommunicationAnalytics(
  options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS
) {
  const [data, setData] = useState<CommunicationAnalyticsMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const scopedOptions = useMemo(() => ({
    ...options,
    // Set school scope for school admins
    ...(isSchoolAdmin && user?.school_id && { schoolId: user.school_id.toString() }),
    // Set district scope for district admins
    ...(isDistrictAdmin && user?.district_id && { districtId: user.district_id.toString() }),
  }), [options, isSchoolAdmin, isDistrictAdmin, user?.school_id, user?.district_id]);

  useEffect(() => {
    const loadCommunicationData = async () => {
      try {
        setLoading(true);
        // This would normally call a communication analytics API
        // For now, return mock data
        setData({
          messages_sent: 0,
          messages_received: 0,
          response_rate: 0,
          engagement_score: 0,
        });
        setError(null);
      } catch (err) {
        console.error('Failed to load communication analytics:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load analytics'
        );
      } finally {
        setLoading(false);
      }
    };

    loadCommunicationData();
  }, [user?.id, scopedOptions]);

  return { data, loading, error, refetch: () => window.location.reload() };
}

// Role-based user metrics
export function useUserMetrics(options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS) {
  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const scopedOptions = useMemo(() => ({
    ...options,
    // Set school scope for school admins
    ...(isSchoolAdmin && user?.school_id && { schoolId: user.school_id.toString() }),
    // Set district scope for district admins
    ...(isDistrictAdmin && user?.district_id && { districtId: user.district_id.toString() }),
  }), [options, isSchoolAdmin, isDistrictAdmin, user?.school_id, user?.district_id]);

  return useAnalytics(scopedOptions);
}

// Role-based evaluation metrics
export function useEvaluationMetrics(options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS) {
  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const scopedOptions = useMemo(() => ({
    ...options,
    // Set school scope for school admins
    ...(isSchoolAdmin && user?.school_id && { schoolId: user.school_id.toString() }),
    // Set district scope for district admins
    ...(isDistrictAdmin && user?.district_id && { districtId: user.district_id.toString() }),
  }), [options, isSchoolAdmin, isDistrictAdmin, user?.school_id, user?.district_id]);

  return useAnalytics(scopedOptions);
}

// Role-based student success analytics
export function useStudentSuccessAnalytics(
  options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS
) {
  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const scopedOptions = useMemo(() => ({
    ...options,
    // Set school scope for school admins
    ...(isSchoolAdmin && user?.school_id && { schoolId: user.school_id.toString() }),
    // Set district scope for district admins
    ...(isDistrictAdmin && user?.district_id && { districtId: user.district_id.toString() }),
  }), [options, isSchoolAdmin, isDistrictAdmin, user?.school_id, user?.district_id]);

  return useAnalytics(scopedOptions);
}

// Performance analytics by school
export function useSchoolPerformanceMetrics(
  schoolId?: string,
  options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS
) {
  const { user, isSchoolAdmin, isDistrictAdmin } = usePermissions();

  const targetSchoolId = schoolId || user?.school_id;

  const scopedOptions = useMemo(() => ({
    ...options,
    // Set school scope for school admins
    ...(isSchoolAdmin && user?.school_id && { schoolId: user.school_id.toString() }),
    // Set district scope for district admins
    ...(isDistrictAdmin && user?.district_id && { districtId: user.district_id.toString() }),
    // Use provided or user's school ID if available
    ...(targetSchoolId && { schoolId: targetSchoolId }),
  }), [options, isSchoolAdmin, isDistrictAdmin, user?.school_id, user?.district_id, targetSchoolId]);

  return useAnalytics(scopedOptions);
}

// District-wide analytics (admin only)
export function useDistrictAnalytics(options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS) {
  const { user, isDistrictAdmin } = usePermissions();

  const scopedOptions = useMemo(() => ({
    ...options,
    includeSubordinates: true,
    ...(user?.district_id && { districtId: user.district_id.toString() }),
  }), [options, user?.district_id]);

  // Always call the hook first to maintain React hooks rules
  const analyticsResult = useAnalytics(scopedOptions);

  // Then check authorization and return appropriate result
  if (!isDistrictAdmin) {
    return {
      data: null,
      loading: false,
      error: 'Unauthorized: District admin access required',
      refresh: () => {
        // No-op refresh for unauthorized users
        console.warn('Unauthorized access attempt to district analytics');
      },
    };
  }

  return analyticsResult;
}

// Export utilities for report generation
export function useReportExport() {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, hasPermission } = usePermissions();

  const exportData = useCallback(
    async (
      reportType: 'users' | 'evaluations' | 'engagement' | 'performance',
      format: 'csv' | 'pdf' | 'xlsx' = 'csv',
      options: RoleBasedAnalyticsOptions = EMPTY_OPTIONS
    ) => {
      if (!hasPermission('report:export')) {
        setError('Insufficient permissions to export reports');
        return null;
      }

      setExporting(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams({
          type: reportType,
          format,
          role: user?.role || '',
        });

        // Add role-specific parameters
        if (user?.role === 'school_admin' && user.school_id) {
          queryParams.append('school_id', user.school_id.toString());
        } else if (user?.role === 'district_admin' && user.district_id) {
          queryParams.append('district_id', user.district_id.toString());
        }

        if (options.dateRange) {
          queryParams.append(
            'start_date',
            options.dateRange.start.toISOString()
          );
          queryParams.append('end_date', options.dateRange.end.toISOString());
        }

        const response = await fetch(
          `/api/analytics/export?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Export failed');
        }

        // Handle file download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}_report_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Export failed');
        return null;
      } finally {
        setExporting(false);
      }
    },
    [user, hasPermission]
  );

  return {
    exportData,
    exporting,
    error,
  };
}

export default useAnalytics;
